import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Hint} from '@/components/ui/hint'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {FotoAnalysisChip} from '@/components/foto-analysis-chip'
import {PhotoUploadField} from '@/components/photo-upload-field'
import {
  MAX_FOTOS_POR_CONTROL,
  useControlFotoAnalysisAction,
  useDeleteControlFoto,
  useUploadControlFoto,
  type ControlFoto,
} from '@/hooks/sgsst/riesgos/use-ipevr'
import {formatSize} from '@/hooks/sgsst/evidencias/use-evidence'
import {translateApiError} from '@/lib/errors'

export function ControlEvidenceBadge({
  businessId,
  controlId,
  evidenciaHint,
  fotos,
  canEdit,
}: {
  businessId: string
  controlId: string
  evidenciaHint: string | null
  fotos: ControlFoto[]
  canEdit: boolean
}) {
  const {t} = useTranslation()
  const [expanded, setExpanded] = useState(false)
  if (!evidenciaHint) return null
  const hasFotos = fotos.length > 0

  // `contents`: el botón y el panel entran directo a la fila flex del chip del control.
  return (
    <div className="contents" data-testid={`control-evidence-${controlId}`}>
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          data-testid={`control-evidence-toggle-${controlId}`}
          className={
            hasFotos
              ? 'inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 font-mono text-[10px] font-medium text-muted-foreground uppercase hover:bg-accent hover:text-accent-foreground'
              : 'tone-amber inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[10px] font-medium uppercase'
          }>
          {hasFotos ? t('ipevr.controles.evidenciaCount', {count: fotos.length}) : t('ipevr.controles.sinEvidencia')}
        </button>
      </span>

      {expanded && (
        <div className="w-full basis-full">
          <ControlEvidenceExpanded
            businessId={businessId}
            controlId={controlId}
            evidenciaHint={evidenciaHint}
            fotos={fotos}
            canEdit={canEdit}
          />
        </div>
      )}
    </div>
  )
}

function ControlEvidenceExpanded({
  businessId,
  controlId,
  evidenciaHint,
  fotos,
  canEdit,
}: {
  businessId: string
  controlId: string
  evidenciaHint: string
  fotos: ControlFoto[]
  canEdit: boolean
}) {
  const {t} = useTranslation()
  const upload = useUploadControlFoto(businessId)
  const deleteFoto = useDeleteControlFoto(businessId)
  const analysisAction = useControlFotoAnalysisAction(businessId)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<ControlFoto | null>(null)

  return (
    <div
      className="rounded-lg border border-border bg-secondary/30 p-3"
      data-testid={`control-evidence-panel-${controlId}`}>
      <Hint testId={`control-evidence-hint-${controlId}`}>
        {t('ipevr.controles.evidenciaHelp', {hint: evidenciaHint})}
      </Hint>
      {fotos.length > 0 && (
        <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
          {fotos.map((foto) => (
            <div key={foto.id} className="space-y-1" data-testid="control-evidence-item">
              <img
                src={foto.download_url}
                alt={foto.filename}
                loading="lazy"
                className="h-16 w-full rounded-md object-cover"
              />
              <p className="truncate font-mono text-[9px] text-muted-foreground" title={foto.filename}>
                {formatSize(foto.size_bytes)}
              </p>
              <FotoAnalysisChip
                foto={foto}
                hint={evidenciaHint}
                editable={canEdit}
                onDismiss={() => analysisAction.mutate({controlId, fotoId: foto.id, action: 'dismiss'})}
                onRetry={() => analysisAction.mutate({controlId, fotoId: foto.id, action: 'reanalyze'})}
              />
              {canEdit && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="w-full text-[11px]"
                  data-testid="control-evidence-delete"
                  onClick={() => setDeleting(foto)}>
                  {t('ipevr.controles.evidenciaRemove')}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {canEdit && fotos.length < MAX_FOTOS_POR_CONTROL && (
        <div className="mt-2.5">
          <PhotoUploadField
            testId="control-evidence"
            inputTestId="control-evidence-input"
            buttonTestId="control-evidence-add"
            buttonLabel={t('ipevr.controles.evidenciaAdd')}
            listAttached={false}
            onUpload={(file, onProgress) => upload.mutateAsync({controlId, file, onProgress})}
          />
        </div>
      )}
      {deleteError && (
        <p className="mt-1.5 text-xs text-destructive" data-testid="control-evidence-delete-error">
          {deleteError}
        </p>
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t('ipevr.controles.evidenciaDeleteTitle')}
        description={t('ipevr.controles.evidenciaDeleteBody')}
        confirmLabel={t('ipevr.controles.evidenciaDeleteConfirm')}
        cancelLabel={t('common.cancel')}
        pending={deleteFoto.isPending}
        onConfirm={() =>
          deleting &&
          deleteFoto.mutate(
            {controlId, fotoId: deleting.id},
            {onSuccess: () => setDeleting(null), onError: (err) => setDeleteError(translateApiError(err))}
          )
        }
      />
    </div>
  )
}
